import {
    ActionIcon,
    Anchor,
    Breadcrumbs,
    Center,
    Group,
    Menu,
    Stack,
    Text,
    Tooltip,
    useMantineColorScheme,
} from '@mantine/core';
import { useElementSize, useViewportSize } from '@mantine/hooks';
import {
    IconChevronRight,
    IconHome,
    IconLogout,
    IconMessageReport,
    IconMoon,
    IconShieldCog,
    IconSun,
    IconUser,
} from '@tabler/icons-react';
import * as React from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Show } from '../../../../../../react-commons/components/control-flow/Show';
import { When } from '../../../../../../react-commons/components/control-flow/When';
import { useActivitiesActions } from '../../../../resource/resources/artifacts/activities/activitiesResource';
import { useProcessAreaActions } from '../../../../resource/resources/artifacts/process_area/processAreaResource';
import { useProjectActions } from '../../../../resource/resources/artifacts/project/projectResource';
import { useProjectVersionPublishmentActions } from '../../../../resource/resources/artifacts/project/projectVersionPublishmentResource';
import { useProjectVersionActions } from '../../../../resource/resources/artifacts/project/projectVersionResource';
import { useWorkflowRepresentationActions } from '../../../../resource/resources/artifacts/use_case/representation/workflowRepresentationResource';
import { useUseCaseActions } from '../../../../resource/resources/artifacts/use_case/useCaseResource';
import { useUserActions } from '../../../../resource/resources/persistables/user/userResource';
import { TOOLTIPS } from '../../../../resource/tooltipTexts';
import { useAuth } from '../../../authentication/AuthProvider';
import { useUserId } from '../../../authentication/useUserId';
import { MyLink, RouteInfo, SomeRouteInfo, useCurrentRoute } from '../../../router/createdRouter';
import type { Route } from '../../../router/route';
import { SystemAnnouncementButton } from './SystemAnnouncementButton';

export const getBreadCrumb = <R extends Route>(
    label: string | undefined,
    routeInfo: RouteInfo<R> | undefined,
    textColor = 'white'
) => {
    const anchor = (
        <Anchor component={'div'} pb={2} fw={500} c={textColor} key={routeInfo?.route ?? label}>
            {label}
        </Anchor>
    );

    // fixes Each child in a list should have a unique "key" prop
    return routeInfo ?
            <MyLink key={`${routeInfo.route}_${label}`} {...routeInfo}>
                {anchor}
            </MyLink>
        :   anchor;
};

// TODO refactor
export const MenuBarComponent = () => {
    const [breadCrumbWidthNotEllipsed, setBreadCrumbWidthNotEllipsed] = React.useState<number>(0);
    const { width: windowWidth } = useViewportSize();
    const { ref: breadCrumbRef, width: breadCrumbWidth } = useElementSize();
    const currentRoute = useCurrentRoute();
    const {
        id: projectId,
        versionId,
        workflowId,
        useCaseObjectId,
        workflowObjectId,
        processAreaId,
        processAreaObjectId,
        activitiesId,
        standardId,
        useCaseId,
    } = useParams<{
        id: string | undefined;
        versionId: string | undefined;
        workflowId: string | undefined;
        useCaseObjectId: string | undefined;
        workflowObjectId: string | undefined;
        processAreaId: string | undefined;
        processAreaObjectId: string | undefined;
        activitiesId: string | undefined;
        standardId: string | undefined;
        useCaseId: string | undefined;
    }>();
    const [search] = useSearchParams();
    const { toggleColorScheme, colorScheme } = useMantineColorScheme();

    const { logout } = useAuth();

    const userId = useUserId('');
    const isAuthenticated = Boolean(userId);

    const userActions = useUserActions();
    const projectActions = useProjectActions();
    const projectVersionActions = useProjectVersionActions();
    const projectVersionPublishmentActions = useProjectVersionPublishmentActions();
    const useCaseActions = useUseCaseActions();
    const workflowActions = useWorkflowRepresentationActions();
    const processAreaActions = useProcessAreaActions();
    const activityActions = useActivitiesActions();

    const userByToken = userActions.useGetResourceByToken();
    const userSystemRights = userActions.useGetUserEffectiveRightsOfUser(userId, projectId);
    const projectQuery = projectActions.useGetResource(projectId);
    const projectsQuery = projectActions.useGetAllResources();
    const useCaseQuery = useCaseActions.useGetResource(useCaseId);
    const processAreaQuery = processAreaActions.useGetResource(processAreaId);
    const activityQuery = activityActions.useGetResource(activitiesId);
    const projectVersionQuery = projectVersionActions.useGetResource(versionId);
    const projectVersionByPublishmentQuery = projectVersionActions.useGetByPublishment(
        search.get('publishment'),
        projectId
    );
    const projectVersionsQuery = projectVersionActions.useGetAllResources(projectId);

    const getProjectVersion = () => {
        if (projectVersionQuery.data) {
            return projectVersionQuery.data;
        }
        if (projectVersionByPublishmentQuery.data) {
            return projectVersionByPublishmentQuery.data;
        }

        const versionData = projectVersionsQuery.data;
        return versionData && versionData.length ? versionData[versionData.length - 1] : undefined;
    };
    const projectVersionPublishmentQuery = projectVersionPublishmentActions.useGetSingleResource(
        getProjectVersion()?.id
    );
    const useCaseByObjectIdQuery = useCaseActions.useGetResourceByObjectIdAndVersionedIn(
        useCaseObjectId,
        getProjectVersion()?.id
    );
    const workflowByIdQuery = workflowActions.useGetResource(workflowId);
    const workflowByObjectIdQuery = workflowActions.useGetResourceByObjectIdAndVersionedIn(
        workflowObjectId,
        getProjectVersion()?.id
    );
    const processAreaByObjectIdQuery = processAreaActions.useGetResourceByObjectIdAndVersionedIn(
        processAreaObjectId,
        getProjectVersion()?.id
    );

    const isNotLatestVersion = () => {
        const currentVersion = getProjectVersion();
        const foundLaterVersion =
            currentVersion && projectVersionsQuery.data ?
                projectVersionsQuery.data[projectVersionsQuery.data.length - 1].id !== currentVersion.id
            :   undefined;

        return !!(
            currentVersion &&
            foundLaterVersion &&
            userSystemRights.data &&
            !userSystemRights.data.isProcessAdmin &&
            !userSystemRights.data.isManager
        );
    };

    const getBreadCrumbs = (): Array<React.ReactNode> | undefined => {
        const projectsLink = getBreadCrumb('Projects', {
            route: '/projects',
            searchParams: {
                selected: projectId,
            },
        });

        const adminPanelLink = getBreadCrumb('Admin Panel', { route: '/admin-panel' });

        const getSingleProjectLabelAndRouteInfo = (withVersion: boolean | undefined, noOnClick?: boolean) => {
            const routeInfo =
                noOnClick || !projectId ? undefined : (
                    ((!withVersion || !versionId ?
                        {
                            route: '/project/:id',
                            routeParams: { id: projectId },
                        }
                    : standardId && useCaseId ?
                        {
                            route: '/project/:id/active-version/:versionId/standard/:standardId/use-case/:useCaseId',
                            routeParams: { id: projectId, versionId, standardId, useCaseId },
                        }
                    :   {
                            route: '/project/:id/active-version/:versionId',
                            routeParams: { id: projectId, versionId },
                        }) satisfies SomeRouteInfo)
                );

            return {
                label:
                    !withVersion ? projectQuery.data?.name
                    : (
                        (search.get('publishment') || !userSystemRights.data?.isProcessAdmin) &&
                        projectsQuery.data?.length === 1
                    ) ?
                        (projectVersionPublishmentQuery.data?.title ?? 'Not Published')
                    : !userSystemRights.data?.isProcessAdmin ?
                        `${projectQuery.data?.name} : ${projectVersionPublishmentQuery.data?.title ?? 'Not Published'}`
                    :   `${projectQuery.data?.name} (${getProjectVersion()?.versionInfo.versionNumber})`,
                routeInfo: routeInfo,
            };
        };
        const getSingleProjectLink = (withVersion?: boolean, noOnClick?: boolean) => {
            const { label, routeInfo } = getSingleProjectLabelAndRouteInfo(withVersion, noOnClick);
            return getBreadCrumb(label, routeInfo);
        };

        const getUseCaseLink = (noOnClick?: boolean, publishedLink?: boolean) => {
            const publishment = search.get('publishment');

            const routeInfo =
                noOnClick || !projectId ? undefined : (
                    ((publishedLink && useCaseObjectId && publishment ?
                        {
                            route: '/project/:id/published-use-case/:useCaseObjectId',
                            routeParams: { id: projectId, useCaseObjectId },
                            searchParams: { publishment },
                        }
                    :   {
                            route: '/project/:id/active-version/:versionId/standard/:standardId/use-case/:useCaseId/workflow-representations',
                            routeParams: {
                                id: projectId,
                                versionId: versionId ?? '',
                                standardId: standardId ?? '',
                                useCaseId: useCaseId ?? '',
                            },
                            searchParams: {},
                        }) satisfies SomeRouteInfo)
                );

            return getBreadCrumb(
                `Use Case : ${useCaseQuery.data?.name ?? useCaseByObjectIdQuery.data?.name}`,
                routeInfo
            );
        };

        const getWorkflowRepresentationLink = (noOnClick?: boolean, publishedLink?: boolean) => {
            const publishment = search.get('publishment');

            const routeInfo =
                noOnClick || !projectId ? undefined : (
                    ((publishedLink && useCaseObjectId && publishment && workflowObjectId ?
                        {
                            route: '/project/:id/published-use-case/:useCaseObjectId/published-workflow-representation/:workflowObjectId',
                            routeParams: { id: projectId, useCaseObjectId, workflowObjectId },
                            searchParams: { publishment },
                        }
                    :   {
                            route: '/project/:id/active-version/:versionId/standard/:standardId/use-case/:useCaseId/workflow-representations',
                            routeParams: {
                                id: projectId,
                                versionId: versionId ?? '',
                                standardId: standardId ?? '',
                                useCaseId: useCaseId ?? '',
                            },
                            searchParams: { selected: workflowId },
                        }) satisfies SomeRouteInfo)
                );

            return getBreadCrumb(
                `Workflow Representation : ${workflowByObjectIdQuery.data?.name ?? workflowByIdQuery.data?.name}`,
                routeInfo
            );
        };

        const getProcessAreaLink = (noOnClick?: boolean) => {
            const routeInfo =
                noOnClick || !projectId ? undefined : (
                    ({
                        route: '/project/:id/active-version/:versionId/process-area/:processAreaId/standard/:standardId/use-case/:useCaseId',
                        routeParams: {
                            id: projectId,
                            versionId: versionId ?? '',
                            processAreaId: processAreaId ?? '',
                            standardId: standardId ?? '',
                            useCaseId: useCaseId ?? '',
                        },
                        searchParams: {},
                    } satisfies SomeRouteInfo)
                );

            return getBreadCrumb(
                `Process Area : ${processAreaQuery.data?.name ?? processAreaByObjectIdQuery.data?.name}`,
                routeInfo
            );
        };

        const getActivitiesModelLink = (noOnClick?: boolean) => {
            const routeInfo =
                noOnClick || !projectId ? undefined : (
                    ((standardId && useCaseId ?
                        {
                            route: '/project/:id/active-version/:versionId/activities-model/:activitiesId/standard/:standardId/use-case/:useCaseId',
                            routeParams: {
                                id: projectId,
                                versionId: versionId ?? '',
                                activitiesId: activitiesId ?? '',
                                standardId,
                                useCaseId,
                            },
                        }
                    :   {
                            route: '/project/:id/active-version/:versionId/activities-model/:activitiesId',
                            routeParams: {
                                id: projectId,
                                versionId: versionId ?? '',
                                activitiesId: activitiesId ?? '',
                            },
                        }) satisfies SomeRouteInfo)
                );

            return getBreadCrumb(`Activities Model : ${activityQuery.data?.name}`, routeInfo);
        };

        const redirected = Boolean(search.get('redirected'));
        switch (currentRoute) {
            case '/admin-panel': {
                return [adminPanelLink];
            }
            case '/admin-panel/system-settings': {
                return [adminPanelLink, getBreadCrumb('System Settings', { route: '/admin-panel/system-settings' })];
            }
            case '/admin-panel/system-right-management': {
                return [
                    adminPanelLink,
                    getBreadCrumb('System Right Management', { route: '/admin-panel/system-right-management' }),
                ];
            }
            case '/admin-panel/system-announcements': {
                return [
                    adminPanelLink,
                    getBreadCrumb('System Announcements', { route: '/admin-panel/system-announcements' }),
                ];
            }
            case '/project-user-management/:id': {
                return [projectsLink, getSingleProjectLink()];
            }
            case '/projects': {
                return [projectsLink];
            }
            case '/project/:id/published-use-case/:useCaseObjectId': {
                return [getSingleProjectLink(true, true), getUseCaseLink(true)];
            }
            case '/project/:id/published-use-case/:useCaseObjectId/published-workflow-representation/:workflowObjectId': {
                return [getSingleProjectLink(true, true), getUseCaseLink(true), getWorkflowRepresentationLink(true)];
            }
            case '/project/:id/active-version/:versionId/standard/:standardId/use-case/:useCaseId/workflow-representations': {
                return [projectsLink, getSingleProjectLink(true, redirected), getUseCaseLink(true)];
            }
            case '/project/:id/published-process-area/:processAreaObjectId': {
                return [getSingleProjectLink(true, true), getProcessAreaLink(true)];
            }
            case '/project/:id/published-process-area/:processAreaObjectId/published-use-case/:useCaseObjectId': {
                return [getSingleProjectLink(true), getUseCaseLink(false, true), getProcessAreaLink(true)];
            }
            case '/project/:id/published-process-area/:processAreaObjectId/published-use-case/:useCaseObjectId/published-workflow-representation/:workflowObjectId': {
                return [
                    getSingleProjectLink(true),
                    getUseCaseLink(false, true),
                    getWorkflowRepresentationLink(false, true),
                    getProcessAreaLink(true),
                ];
            }
            case '/project/:id/active-version/:versionId/process-area/:processAreaId': {
                return [projectsLink, getSingleProjectLink(true, redirected), getProcessAreaLink(true)];
            }
            case '/project/:id/active-version/:versionId/process-area/:processAreaId/standard/:standardId/use-case/:useCaseId': {
                return [
                    projectsLink,
                    getSingleProjectLink(true, redirected),
                    getUseCaseLink(),
                    getProcessAreaLink(true),
                ];
            }
            case '/project/:id/active-version/:versionId/process-area/:processAreaId/standard/:standardId/use-case/:useCaseId/workflow-representation/:workflowId': {
                return [
                    projectsLink,
                    getSingleProjectLink(true, redirected),
                    getUseCaseLink(),
                    getWorkflowRepresentationLink(),
                    getProcessAreaLink(true),
                ];
            }
            case '/project/:id/active-version/:versionId/activities-model/:activitiesId': {
                return [projectsLink, getSingleProjectLink(true, redirected), getActivitiesModelLink(true)];
            }
            case '/project/:id/active-version/:versionId/activities-model/:activitiesId/standard/:standardId/use-case/:useCaseId': {
                return [
                    projectsLink,
                    getSingleProjectLink(true, redirected),
                    getUseCaseLink(),
                    getActivitiesModelLink(true),
                ];
            }
            case '/project/:id':
            case '/project/:id/active-version/:versionId':
            case '/project/:id/active-version/:versionId/standard/:standardId/use-case/:useCaseId': {
                return [projectsLink, getSingleProjectLink(true, redirected)];
            }
            case '/auth/oidc/callback/login':
            case '/':
            case '*': {
                return undefined;
            }
        }
    };

    const fileMenu = () => {
        const canNavigateToProjectsPage = currentRoute !== '/projects';

        const logo = (
            <Tooltip label={TOOLTIPS.PROJECTS} disabled={!canNavigateToProjectsPage}>
                <IconHome style={{ color: 'white', cursor: canNavigateToProjectsPage ? 'pointer' : undefined }} />
            </Tooltip>
        );

        return (
            <Group wrap={'nowrap'}>
                <Show when={!search.get('publishment')}>
                    <Show when={canNavigateToProjectsPage} fallback={logo}>
                        <MyLink
                            route={'/projects'}
                            searchParams={{
                                selected: projectId,
                            }}
                        >
                            {logo}
                        </MyLink>
                    </Show>
                </Show>
                <Breadcrumbs ref={breadCrumbRef} separator={<IconChevronRight size={16} style={{ color: 'white' }} />}>
                    {getBreadCrumbs()}
                </Breadcrumbs>
            </Group>
        );
    };

    //TODO: This logic can be worked on a bit I think.
    React.useEffect(() => {
        setBreadCrumbWidthNotEllipsed(0);
    }, [window.location.pathname, projectId]);

    React.useEffect(() => {
        if (breadCrumbWidth > breadCrumbWidthNotEllipsed) {
            setBreadCrumbWidthNotEllipsed(breadCrumbWidth);
        }
    }, [breadCrumbWidth, windowWidth]);

    return (
        <Stack w={'100%'} h={40} gap={0}>
            <Group
                wrap={'nowrap'}
                h={isNotLatestVersion() ? 28 : 40}
                p={6}
                bg={'primaryTeal'}
                justify={'space-between'}
            >
                {fileMenu()}
                <Group wrap={'nowrap'}>
                    <Show when={!search.get('publishment')}>{isAuthenticated && <SystemAnnouncementButton />}</Show>
                    <Show when={getProjectVersion()?.feedbackLink}>
                        <Tooltip label={'Process Feedback'}>
                            <ActionIcon
                                component="a"
                                color="inherit"
                                variant={'transparent'}
                                pt={1}
                                pr={1}
                                size={'sm'}
                                href={getProjectVersion()?.feedbackLink ?? ''}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <IconMessageReport />
                            </ActionIcon>
                        </Tooltip>
                    </Show>
                    <Tooltip label={TOOLTIPS.COLOR_SCHEME}>
                        <ActionIcon
                            pl={2}
                            color="inherit"
                            variant={'transparent'}
                            size={'sm'}
                            onClick={toggleColorScheme}
                        >
                            <When expression={colorScheme} cases={{ dark: <IconSun /> }} fallback={<IconMoon />} />
                        </ActionIcon>
                    </Tooltip>
                    {!search.get('publishment') &&
                        isAuthenticated &&
                        userSystemRights.data &&
                        (userSystemRights.data.isAdmin || userSystemRights.data.isServerAdmin ?
                            <MyLink route={'/admin-panel'}>
                                <Tooltip label={TOOLTIPS.ADMIN_PANEL}>
                                    <ActionIcon color="inherit" variant={'transparent'} size={'md'}>
                                        <IconShieldCog />
                                    </ActionIcon>
                                </Tooltip>
                            </MyLink>
                        :   undefined)}
                    <Menu>
                        <Menu.Target>
                            <Tooltip label={TOOLTIPS.USER}>
                                <ActionIcon color="inherit" variant={'transparent'} size={'sm'}>
                                    <IconUser />
                                </ActionIcon>
                            </Tooltip>
                        </Menu.Target>
                        <Menu.Dropdown>
                            <Tooltip
                                withArrow
                                arrowSize={8}
                                arrowOffset={15}
                                position={'left-start'}
                                label={
                                    <div style={{ display: 'flex', width: '100%', flexDirection: 'column' }}>
                                        <Show when={userByToken.data?.userDetails?.name}>
                                            {(name) => (
                                                <Group style={{ textAlign: 'right' }}>
                                                    <Text c={'var(--mantine-text-reverse-secondary-color)'} w={100}>
                                                        Name:
                                                    </Text>
                                                    <Text c={'var(--mantine-text-reverse-secondary-color)'}>
                                                        {name}
                                                    </Text>
                                                </Group>
                                            )}
                                        </Show>
                                        <Group style={{ textAlign: 'right' }}>
                                            <Text c={'var(--mantine-text-reverse-secondary-color)'} w={100}>
                                                User Name:
                                            </Text>
                                            <Text c={'var(--mantine-text-reverse-secondary-color)'}>
                                                {userByToken.data?.userName}
                                            </Text>
                                        </Group>
                                        <Show when={userByToken.data?.userDetails?.email}>
                                            {(email) => (
                                                <Group style={{ textAlign: 'right' }}>
                                                    <Text c={'var(--mantine-text-reverse-secondary-color)'} w={100}>
                                                        Email:
                                                    </Text>
                                                    <Text c={'var(--mantine-text-reverse-secondary-color)'}>
                                                        {email}
                                                    </Text>
                                                </Group>
                                            )}
                                        </Show>
                                    </div>
                                }
                            >
                                <Menu.Item>
                                    {userByToken.data?.userDetails?.name ||
                                        userByToken.data?.userName ||
                                        userByToken.data?.id}
                                </Menu.Item>
                            </Tooltip>
                            <Menu.Divider />
                            <Menu.Item leftSection={<IconLogout />} onClick={logout}>
                                Logout
                            </Menu.Item>
                        </Menu.Dropdown>
                    </Menu>
                </Group>
            </Group>
            <Show when={isNotLatestVersion()}>
                <Center w={'100%'} h={12} bg={'var(--mantine-color-error)'}>
                    <Text c={'white'} fz={11}>
                        Currently displayed process version &#34;{projectVersionPublishmentQuery.data?.title}&#34; is
                        not the latest version.
                    </Text>
                </Center>
            </Show>
        </Stack>
    );
};
